import { useCallback, useState } from 'react'
import {
  createCustomerSupportRequest,
  fetchMySupportRequests,
  type CustomerSupportRequest,
} from '../customerSupportApi'
import { asSupportErrorMessage } from '../helpers/errors'
import { useCustomerProtectedList } from './useCustomerProtectedList'

const ORDER_ID_REGEX = /^[a-f0-9]{24}$/i

type UseCustomerSupportInput = {
  authenticated: boolean
  loadSupportHistory: boolean
}

export type UseCustomerSupportResult = {
  supportRequests: CustomerSupportRequest[]
  supportLoading: boolean
  supportError: string
  supportSubmitting: boolean
  supportSuccess: string
  supportSubject: string
  supportMessage: string
  supportOrderId: string
  supportCategory: string
  supportPriority: string
  setSupportSubject: (value: string) => void
  setSupportMessage: (value: string) => void
  setSupportOrderId: (value: string) => void
  setSupportCategory: (value: string) => void
  setSupportPriority: (value: string) => void
  submitSupportRequest: () => void
  retrySupportRequests: () => void
}

export const useCustomerSupport = ({
  authenticated,
  loadSupportHistory,
}: UseCustomerSupportInput): UseCustomerSupportResult => {
  const fetchSupportRequests = useCallback(
    () => fetchMySupportRequests({ limit: 50 }),
    [],
  )

  const {
    items: supportRequests,
    setItems: setSupportRequests,
    loading: supportLoading,
    error: supportError,
    setError: setSupportError,
    retry: retrySupportRequests,
  } = useCustomerProtectedList<CustomerSupportRequest>({
    enabled: authenticated && loadSupportHistory,
    token: null,
    fetchItems: fetchSupportRequests,
    toErrorMessage: asSupportErrorMessage,
    requireToken: false,
  })
  const [supportSubmitting, setSupportSubmitting] = useState(false)
  const [supportSuccess, setSupportSuccess] = useState('')
  const [supportSubject, setSupportSubject] = useState('')
  const [supportMessage, setSupportMessage] = useState('')
  const [supportOrderId, setSupportOrderId] = useState('')
  const [supportCategory, setSupportCategory] = useState('general')
  const [supportPriority, setSupportPriority] = useState('normal')

  const submitSupportRequest = () => {
    const normalizedSubject = supportSubject.trim()
    const normalizedMessage = supportMessage.trim()
    const normalizedOrderId = supportOrderId.trim()

    if (normalizedSubject.length < 3) {
      setSupportError('Oggetto troppo corto (minimo 3 caratteri)')
      return
    }

    if (normalizedMessage.length < 10) {
      setSupportError('Messaggio troppo corto (minimo 10 caratteri)')
      return
    }

    if (normalizedOrderId && !ORDER_ID_REGEX.test(normalizedOrderId)) {
      setSupportError('ID ordine non valido')
      return
    }

    setSupportSubmitting(true)
    setSupportError('')
    setSupportSuccess('')

    void createCustomerSupportRequest(
      {
        subject: `[${supportPriority.toUpperCase()}][${supportCategory.toUpperCase()}] ${normalizedSubject}`,
        message: normalizedMessage,
        orderId: normalizedOrderId || undefined,
      },
    )
      .then((createdSupportRequest) => {
        setSupportRequests((current) => [
          createdSupportRequest,
          ...current.filter((entry) => entry.id !== createdSupportRequest.id),
        ])
        setSupportSubject('')
        setSupportMessage('')
        setSupportOrderId('')
        setSupportCategory('general')
        setSupportPriority('normal')
        setSupportSuccess(`Richiesta inviata con ID ${createdSupportRequest.id}`)
      })
      .catch((nextError) => {
        setSupportError(asSupportErrorMessage(nextError))
      })
      .finally(() => {
        setSupportSubmitting(false)
      })
  }

  return {
    supportRequests,
    supportLoading,
    supportError,
    supportSubmitting,
    supportSuccess,
    supportSubject,
    supportMessage,
    supportOrderId,
    supportCategory,
    supportPriority,
    setSupportSubject,
    setSupportMessage,
    setSupportOrderId,
    setSupportCategory,
    setSupportPriority,
    submitSupportRequest,
    retrySupportRequests,
  }
}
